import React, { memo, useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";

import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ClearIcon from "@mui/icons-material/Clear";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import type { SxProps } from "@mui/material/styles";
import styled from "@mui/material/styles/styled";

import { useDebounce } from "hooks";
import { Editor } from "tinymce";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { misc } from "app/themes/colors";
import { selectCurrentUser } from "features/auth/auth-slice";
import { promptsApi, useGetLastPromptsQuery } from "services/prompts";
import type { Prompt } from "services/types/prompts.type";

import {
  selectAiPromptById,
  setAiPromptDetails
} from "../word-processor.slice";
import AiPromptDialog from "./ai-prompt-dialog.component";

type AiPromptInlineFieldProps = {
  id: string;
  editorRef: React.MutableRefObject<Editor | null>;
  sx?: SxProps;
  onAskAi?: (params: {
    id: string;
    prompt: string;
    fieldContent: string;
    lastResult?: string;
  }) => Promise<void> | undefined;
  placeholder?: string;
  promptType?: number;
  handleChangeValue: (html?: string | undefined, editor?: Editor) => void;
};

const inlineFieldSx: SxProps = {
  width: "100%",
  "& .MuiInputBase-root": {
    height: "34px !important"
  },

  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: grey[300]
    },
    "& .MuiInputBase-input": {
      padding: "5px !important",

      paddingLeft: "10px !important",
      paddingRight: "10px !important"
    },
    "& input.MuiOutlinedInput-input": {
      fontSize: "14px",
      lineHeight: 1.2,
      paddingInlineStart: 0
    }
  }
};

const CustomPopper = styled(Popper)({
  zIndex: 9999
});

const AiPromptInlineField = memo(
  ({
    id,
    editorRef,
    sx,
    onAskAi,
    handleChangeValue,
    placeholder,
    promptType
  }: AiPromptInlineFieldProps) => {
    const currentUser = useAppSelector(selectCurrentUser);
    const dispatch = useAppDispatch();
    const aiPrompt = useSelector((state: any) => selectAiPromptById(state, id));

    const inputRef = useRef<any>();
    const [isMouseHovered, setIsMouseHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const handleSubmit = useCallback(() => {
      const fieldContent =
        editorRef.current?.getContent({ format: "text" }) ?? "";

      if (!(aiPrompt?.prompt ?? "").trim().length) {
        return;
      }

      const result = onAskAi?.({
        id,
        prompt: aiPrompt?.prompt ?? "",
        fieldContent,
        lastResult: aiPrompt?.result
      });

      if (result) {
        result.then(() => {
          dispatch(promptsApi.util.invalidateTags(["Prompt"]));
        });
      }
    }, [aiPrompt?.prompt, aiPrompt?.result, dispatch, editorRef, id, onAskAi]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();
          if ((inputRef.current?.value ?? "").trim().length) {
            handleSubmit();
          }
        }
      },
      [handleSubmit]
    );

    const { value: dSearch } = useDebounce(aiPrompt?.prompt ?? "");

    const { data: lastPrompts = [] } = useGetLastPromptsQuery(
      {
        user: currentUser?.id,
        type: Number(promptType),
        term: dSearch
      },
      {
        skip:
          !currentUser?.id || currentUser?.id === -1 || promptType === undefined
      }
    );

    const filterOptions = createFilterOptions({
      matchFrom: "any",
      stringify: (option: Prompt) => option.prompt
    });

    const isHighlighted = isMouseHovered || isFocused;

    return (
      <>
        <Stack
          direction="row"
          columnGap={1.5}
          width="100%"
          alignItems="center"
          sx={{
            px: 2.3,
            py: 2.5,
            borderBottomLeftRadius: "4px",
            background: isHighlighted
              ? misc.lavenderMistDark
              : misc.lavenderMist,
            transform: "translateY(-32px)",
            boxSizing: "border-box",
            height: "30px",
            ...sx
          }}
          onMouseEnter={() => {
            setIsMouseHovered(true);
          }}
          onMouseLeave={() => {
            setIsMouseHovered(false);
          }}
          onClick={() => {
            inputRef.current?.focus();
          }}
        >
          <AutoAwesomeIcon
            sx={{
              fontSize: "18px",
              color: isHighlighted ? misc.blackAsphalt : grey[800]
            }}
          />
          <Typography
            sx={{
              ml: -0.8,
              color: isHighlighted ? misc.blackAsphalt : grey[800],
              cursor: "pointer"
            }}
          >
            Ask&nbsp;AI:
          </Typography>

          <Autocomplete
            PopperComponent={CustomPopper}
            clearOnEscape
            clearIcon={
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  dispatch(
                    setAiPromptDetails({
                      id,
                      prompt: ""
                    })
                  );
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            }
            filterOptions={filterOptions}
            freeSolo
            openOnFocus
            onClick={(event) => event.stopPropagation()}
            fullWidth
            popupIcon={<ArrowDropDownIcon />}
            getOptionLabel={(option: Prompt | string) =>
              typeof option === "string" ? option : (option?.prompt ?? "")
            }
            isOptionEqualToValue={(option, value) => {
              if (typeof option === "string" || typeof value === "string") {
                return false;
              }
              return option?.id === value?.id;
            }}
            includeInputInList
            disablePortal={false}
            options={lastPrompts}
            renderOption={(props, option) => {
              return (
                <ListItem
                  {...props}
                  key={typeof option === "string" ? option : option.id}
                >
                  <Typography variant="body2">
                    {typeof option === "string" ? option : option.prompt}
                  </Typography>
                </ListItem>
              );
            }}
            value={aiPrompt?.prompt ?? ""}
            onChange={(event, value) => {
              dispatch(
                setAiPromptDetails({
                  id,
                  prompt:
                    typeof value === "string" ? value : (value?.prompt ?? "")
                })
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={inputRef}
                autoComplete="off"
                size="small"
                sx={inlineFieldSx}
                placeholder={placeholder}
                onKeyDown={handleKeyDown}
                onChange={(event) => {
                  dispatch(
                    setAiPromptDetails({
                      id,
                      prompt: event.target.value
                    })
                  );
                }}
                onFocus={() => {
                  setIsFocused(true);
                }}
                onBlur={() => {
                  setIsFocused(false);
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Stack direction="row" alignItems="center" columnGap={2}>
                      <Box>{params.InputProps.endAdornment}</Box>
                      <IconButton
                        size="small"
                        disabled={!aiPrompt?.prompt?.length}
                        sx={{
                          transform: "translateX(-32px)",
                          display: isHighlighted ? "block" : "none"
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSubmit();
                        }}
                      >
                        <ArrowCircleRightIcon />
                      </IconButton>
                    </Stack>
                  )
                }}
              />
            )}
          />
        </Stack>
        {Boolean(aiPrompt?.dialogOpen) && (
          <AiPromptDialog
            id={id}
            editorRef={editorRef}
            onAskAi={onAskAi}
            handleChangeValue={handleChangeValue}
            promptType={promptType}
          />
        )}
      </>
    );
  }
);

export default AiPromptInlineField;
